import { FileSyncStatus } from '@/common/models/enums';
import type { FileStatus } from '@/hooks/useProjectSync';

/**
 * Casts file status from Electron API string format to FileSyncStatus enum
 * Handles the type mismatch between Electron API (returns strings) and our enum types
 * @param status - Status string from Electron API
 * @returns FileSyncStatus enum value
 */
export function castFileStatus(
  status: string | FileSyncStatus
): FileSyncStatus {
  // If it's already a FileSyncStatus enum value, return as-is
  if (Object.values(FileSyncStatus).includes(status as FileSyncStatus)) {
    return status as FileSyncStatus;
  }

  // Map string values to enum (case-insensitive)
  const statusMap: Record<string, FileSyncStatus> = {
    'up-to-date': FileSyncStatus.UP_TO_DATE,
    behind: FileSyncStatus.BEHIND,
    newer: FileSyncStatus.NEWER,
    deleted: FileSyncStatus.DELETED,
    new: FileSyncStatus.NEW,
    unknown: FileSyncStatus.UNKNOWN,
  };

  return statusMap[status.toLowerCase()] || FileSyncStatus.UNKNOWN;
}

/**
 * Casts an array of file statuses from Electron API format
 * Accepts both FileStatus[] and arrays with string status values
 * @param statuses - Array of file statuses from Electron API (may have string status values)
 * @returns Array of file statuses with properly cast status values
 */
export function castFileStatuses(
  statuses:
    | Array<{
        fileName: string;
        status: string | FileSyncStatus;
        isLocal: boolean;
      }>
    | FileStatus[]
    | null
): FileStatus[] | null {
  if (!statuses) return null;

  return statuses.map((s) => ({
    fileName: s.fileName,
    status: castFileStatus(s.status),
    isLocal: s.isLocal,
  }));
}
